import { LEGEND_LABELS, RADAR_PALETTE } from '../lib/radar-legend';
import styles from './RadarLegend.module.css';

export interface RadarLegendProps {
  /** Snow is only on the map — and so only in the legend — when it's enabled. */
  showSnow: boolean;
}

/**
 * What the colours on the map mean.
 *
 * Discrete swatches rather than a gradient bar, because that's how the tiles
 * are actually drawn: RainViewer bands reflectivity into steps, and a smooth
 * ramp invites a reader to interpolate between them as if the intermediate
 * colours meant something.
 *
 * The scale is qualitative — light to intense, no dBZ, no mm/h. RainViewer
 * composites national radar networks whose reflectivity scaling differs
 * country to country, so a number against a colour on a global mosaic would
 * claim a precision the mosaic doesn't have.
 *
 * There is one palette because RainViewer renders one, whatever its tile URLs
 * claim to accept — see `radar-legend.ts`.
 */
export function RadarLegend({ showSnow }: RadarLegendProps) {
  const palette = RADAR_PALETTE;

  return (
    <div className={styles.legend}>
      <div className={styles.swatches} aria-hidden="true">
        {palette.rain.map((color) => (
          <span key={color} className={styles.swatch} style={{ background: color }} />
        ))}
      </div>

      <div className={styles.scale} aria-hidden="true">
        {LEGEND_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      {/* The visual legend is decorative; this is what a screen reader gets. */}
      <p className={styles.reader}>
        Precipitation intensity runs from light on the left to intense on the right.
        {showSnow ? ' Frozen precipitation is drawn in pale blue.' : ''}
      </p>

      {showSnow && (
        <div className={styles.snow}>
          <div className={styles.snowSwatches} aria-hidden="true">
            {palette.snow.map((color) => (
              <span key={color} className={styles.swatch} style={{ background: color }} />
            ))}
          </div>
          <span className={styles.snowLabel}>Snow &amp; ice</span>
        </div>
      )}
    </div>
  );
}
